/**
 * Blocks Visibility Manager
 *
 * Gestisce la visibilità condizionale dei blocchi della sidebar
 * in base alle regole di business definite nel file Excel
 * https://docs.google.com/spreadsheets/d/1znZye1KuIzFJ9BDt5cKmaB1IfGsSvomD6cH9SeMQ2_g/edit?gid=1401854460#gid=1401854460
 */


/**
 * Determina se il blocco Booker deve essere mostrato
 *
 * Il flag bookerAddressRequired viene iniettato nell'oggetto integration
 * dal checkout.vue di bookingv3, leggendolo da hotel_details nello store.
 * Gli altri portali non lo settano, quindi il blocco resta nascosto.
 *
 * @param {Object} config - Configurazione
 * @param {boolean} config.booker_address_required - Se l'hotel richiede indirizzo del prenotante
 * @returns {boolean} - true se il blocco deve essere visibile
 */
export function should_show_booker_block(config) {
  const { booker_address_required } = config
  return booker_address_required === true
}

/**
 * Determina se il blocco Discount Codes deve essere mostrato
 *
 * Regole (in ordine di priorità):
 * 1. Se utente loggato con codice sconto → SEMPRE visibile
 * 2. Se welfare=true per Vacanze Vadobay → visibile (eccezione brand)
 * 3. Se welfare=true per altri brand → nascosto
 * 4. Se loyalty (points > 0) → nascosto
 * 5. Caso standard → visibile
 *
 * @param {Object} config - Configurazione
 * @param {boolean} config.welfare - Se è contesto welfare puro
 * @param {number} config.points - Punti fedeltà/loyalty disponibili
 * @param {boolean} config.has_login_discount_codes - Se ci sono codici sconto al login
 * @param {string} config.app_name - Nome applicazione per eccezioni brand
 * @returns {boolean} - true se il blocco deve essere visibile
 */
export function should_show_discount_codes_block(config) {
  const {
    welfare,
    points = 0,
    has_login_discount_codes,
    app_name = ''
  } = config

  // PRIORITÀ 1: Se utente loggato con codice sconto, mostra sempre
  if (has_login_discount_codes) return true

  // PRIORITÀ 2: Eccezione Vacanze Vadobay - welfare=true mostra comunque i codici sconto
  if (welfare && app_name === 'liveat') return true

  // PRIORITÀ 3: Nasconde se welfare=true per altri brand
  if (welfare) return false

  // PRIORITÀ 4: Nasconde se loyalty senza codice sconto al login
  if (points > 0) return false

  // PRIORITÀ 5: Caso standard - mostra sempre
  return true
}

/**
 * Determina se il blocco Bill to Pax deve essere mostrato
 *
 * Regole:
 * - NON mostrare su Booking (livebk) - usa booker invece
 * - Mostra se c'è integrazione carta in welfare=false
 * - Mostra se ci sono buoni sconto (welfare=false)
 * - Mostra per Jointly/Jakala (welfare=true + billToPax=true)
 * - Mostra se c'è integrazione carta con loyalty/points
 * - Nascondi per welfare puro senza integrazione
 *
 * @param {Object} config - Configurazione
 * @param {string} config.app_name - Nome applicazione
 * @param {boolean} config.bill_to_pax_required - Se billToPax è richiesto dalla config
 * @param {boolean} config.welfare - Se è contesto welfare
 * @param {number} config.credit_card_amount - Importo da pagare con carta
 * @param {number} config.discount_codes_amount - Importo totale buoni sconto
 * @param {number} config.points - Punti loyalty disponibili
 * @returns {boolean} - true se il blocco deve essere visibile
 */
export function should_show_bill_to_pax_block(config) {
  const {
    app_name,
    bill_to_pax_required,
    welfare,
    credit_card_amount = 0,
    discount_codes_amount = 0,
    points = 0
  } = config

  // PRIORITÀ 0: Mai mostrare su Booking e Liveflight - usano booker invece
  if (app_name === 'livebk' || app_name === 'voli-vadobay') return false

  // Caso Jointly/Jakala: welfare=true + billToPax=true
  if (welfare && bill_to_pax_required) return true

  // Welfare puro senza integrazione: nasconde
  if (welfare && credit_card_amount === 0) return false

  // Integrazione carta necessaria (welfare=false o con integrazione)
  if (credit_card_amount > 0) return true

  // Buoni sconto applicati (welfare=false)
  if (discount_codes_amount > 0 && !welfare) return true

  // Loyalty/points con integrazione
  if (points > 0 && credit_card_amount > 0) return true

  return false
}

/**
 * Determina se il blocco Stripe (carta di credito) deve essere mostrato
 *
 * @param {number} credit_card_amount - Importo da pagare con carta
 * @returns {boolean} - true se c'è un importo da pagare con carta
 */
export function should_show_stripe_block(credit_card_amount) {
  return credit_card_amount > 0
}

/**
 * Determina se il blocco OTP deve essere mostrato
 *
 * @param {Object} config - Configurazione OTP
 * @param {boolean} config.otp_required - Se OTP è richiesto
 * @param {boolean|string} config.otp_check - Stato validazione OTP
 * @param {number} config.credit_card_amount - Importo carta di credito
 * @param {boolean} config.stripe_completed - Se Stripe è completato
 * @returns {boolean} - true se OTP deve essere mostrato
 */
export function should_show_otp_block(config) {
  const { otp_required, otp_check, credit_card_amount, stripe_completed } = config

  // Mostro OTP se:
  // - è richiesto dal config e non è stato completato l'iter OTP
  // - c'è un totale da caricare su carta di credito E la procedura stripe è completata
  const otp_completed = otp_check === false
  const credit_card_required = credit_card_amount > 0
  const credit_card_completed = credit_card_required && stripe_completed

  return otp_required && otp_completed && (credit_card_completed || !credit_card_required)
}


/**
 * Genera la configurazione completa dei blocchi della sidebar
 *
 * @param {import('../types').SidebarContext} context - Contesto completo della sidebar
 * @returns {import('../types').BlocksConfiguration} - Configurazione di tutti i blocchi
 */
export function get_blocks_configuration(context) {
  const {
    points,
    credit_card_amount,
    integration,
    app_name,
    otp_required,
    otp_check,
    stripe_completed,
    discount_codes_amount,
  } = context

  const bill_to_pax_required = integration.billToPax || false
  const welfare = integration.welfare || false
  const has_login_discount_codes = !!integration.loginDiscountCode
  const booker_address_required = integration.bookerAddressRequired

  // IMPORTANTE: L'ordine delle chiavi deve rispettare l'ordine di rendering nel template
  return {
    guest: {
      payload: false,
      show: true, // sempre visibile
    },
    booker: {
      payload: false,
      show: should_show_booker_block({ booker_address_required }),
    },
    bill_to_pax: {
      payload: false,
      show: should_show_bill_to_pax_block({
        app_name,
        bill_to_pax_required,
        welfare,
        credit_card_amount,
        discount_codes_amount,
        points
      }),
    },
    discount_codes: {
      payload: true,
      show: should_show_discount_codes_block({
        welfare,
        points,
        has_login_discount_codes,
        app_name
      }),
    },
    stripe: {
      payload: false,
      show: should_show_stripe_block(credit_card_amount),
    },
    payments: {
      payload: true,
      show: true, // sempre visibile
    },
    otp: {
      payload: false,
      show: should_show_otp_block({
        otp_required,
        otp_check,
        credit_card_amount,
        stripe_completed,
      }),
    },
    disclaimers: {
      payload: false,
      show: true, // sempre visibile
    },
  }
}